/**
 * Apply AI detection highlights using marks (visual overlays that don't sync to Y.js)
 * Uses TipTap marks with parseHTML disabled to prevent persistence
 */

/**
 * Map a plain text position range to ProseMirror document positions
 */
function mapTextPositionToDoc(doc, start, length) {
  const ranges = [];
  let textOffset = 0;
  const end = start + length;

  // Traverse document and map text positions
  doc.descendants((node, pos) => {
    if (!node.isText) return;

    const nodeStart = textOffset;
    const nodeEnd = textOffset + node.text.length;

    // Check if this text node overlaps with our target range
    if (nodeEnd > start && nodeStart < end) {
      // Calculate the overlap within this specific text node
      const overlapStart = Math.max(0, start - nodeStart);
      const overlapEnd = Math.min(node.text.length, end - nodeStart);

      // Convert to ProseMirror positions (pos is already the correct position)
      const from = pos + overlapStart;
      const to = pos + overlapEnd;

      ranges.push({ from, to });
    }

    // Increment by actual text length only
    textOffset += node.text.length;
  });

  return ranges;
}

/**
 * Find all occurrences of text in document (case-insensitive)
 */
function findAllOccurrences(docText, searchText) {
  const positions = [];
  let startIndex = 0;

  while (startIndex < docText.length) {
    const foundIndex = docText.indexOf(searchText, startIndex);
    if (foundIndex === -1) break;

    positions.push(foundIndex);
    startIndex = foundIndex + 1; // Move past this occurrence to find overlapping matches
  }

  return positions;
}

export const applyAIHighlights = (editor, sectionAnalysis) => {
  if (!editor || !sectionAnalysis || !Array.isArray(sectionAnalysis)) {
    return;
  }

  // Remove any existing highlights first
  removeAIHighlights(editor);

  const { state, view } = editor;
  const { tr } = state;
  const docText = state.doc.textContent.toLowerCase();

  // Get the mark type
  const markType = state.schema.marks.aiHighlight;
  if (!markType) return;

  // Collect all ranges to highlight
  const rangesToHighlight = [];

  sectionAnalysis.forEach((section) => {
    const { matched_text, category, start_pos, end_pos } = section;

    // Skip if no category or if it's human_written (no need to highlight)
    if (!category || category === 'human_written') return;

    // Use position-based highlighting if available (more accurate)
    if (start_pos !== undefined && end_pos !== undefined) {
      const length = end_pos - start_pos;

      // Map to ProseMirror positions
      const ranges = mapTextPositionToDoc(state.doc, start_pos, length);

      ranges.forEach(({ from, to }) => {
        rangesToHighlight.push({ from, to, category });
      });
    } else if (matched_text) {
      // Fallback to text matching (old approach)
      const searchText = matched_text.toLowerCase().trim();
      if (searchText.length === 0) return;

      // Find ALL occurrences of this text
      const occurrences = findAllOccurrences(docText, searchText);
      if (occurrences.length === 0) return;

      // Highlight each occurrence
      occurrences.forEach((textPos) => {
        // Map to ProseMirror positions
        const ranges = mapTextPositionToDoc(state.doc, textPos, searchText.length);

        ranges.forEach(({ from, to }) => {
          rangesToHighlight.push({ from, to, category });
        });
      });
    }
  });

  // Apply all marks in a single transaction without changing selection
  let transaction = tr;
  rangesToHighlight.forEach(({ from, to, category }) => {
    const mark = markType.create({ category });
    transaction = transaction.addMark(from, to, mark);
  });

  // Dispatch the transaction without scrolling
  view.dispatch(transaction);
};

/**
 * Remove all AI highlight marks
 */
export const removeAIHighlights = (editor) => {
  if (!editor) return;

  const { state, view } = editor;
  const markType = state.schema.marks.aiHighlight;
  if (!markType) return;

  // Remove all aiHighlight marks in a single transaction without changing selection
  const { tr } = state;
  const transaction = tr.removeMark(0, state.doc.content.size, markType);
  view.dispatch(transaction);
};

/**
 * Check if AI highlighting is currently applied
 */
export const hasAIHighlights = (editor) => {
  return false;
};
